import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiFetch, getUserFacingErrorMessage } from './services/apiClient';
import './CreateJobPostingPage.css';

function parseApiErrorDetail(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const d = payload.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join('; ');
    return payload.error || payload.message || null;
}

const CreateJobPostingPage = () => {
    const navigate = useNavigate();
    const { jobId: jobIdParam } = useParams();
    const location = useLocation();
    const existingJobId = jobIdParam ? Number.parseInt(jobIdParam, 10) : null;
    const isExistingJob =
        existingJobId !== null && Number.isFinite(existingJobId) && existingJobId > 0;

    const [jobDescription, setJobDescription] = useState('');
    const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
    const [creating, setCreating] = useState(false);
    const [extractingFile, setExtractingFile] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [error, setError] = useState(null);
    /** Baseline JD from DB (or navigation state) for existing-job update skip/compare */
    const initialDescriptionRef = useRef(null);
    const jobDescriptionFileInputRef = useRef(null);

    const prefilledFromList = location.state?.prefilledDescription;

    useEffect(() => {
        if (!isExistingJob) {
            initialDescriptionRef.current = null;
            return;
        }

        let cancelled = false;

        const load = async () => {
            setPageLoading(true);
            setError(null);
            try {
                if (prefilledFromList !== undefined && prefilledFromList !== null) {
                    const text = String(prefilledFromList);
                    if (!cancelled) {
                        setJobDescription(text);
                        initialDescriptionRef.current = text;
                    }
                    return;
                }

                const response = await apiFetch(`/api/jobs/${existingJobId}`);
                if (!response.ok) {
                    const errBody = await response.json().catch(() => ({}));
                    throw new Error(
                        getUserFacingErrorMessage(
                            response.status,
                            errBody,
                            parseApiErrorDetail(errBody) || 'Could not load this job'
                        )
                    );
                }
                const data = await response.json();
                const text = data.description ?? '';
                if (!cancelled) {
                    setJobDescription(text);
                    initialDescriptionRef.current = text;
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || 'Could not load job');
                    initialDescriptionRef.current = '';
                }
            } finally {
                if (!cancelled) setPageLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [isExistingJob, existingJobId, prefilledFromList]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setJobDescriptionFile(null);
            return;
        }

        setJobDescriptionFile(file);
        setError(null);

        const nameLower = file.name.toLowerCase();
        if (nameLower.endsWith('.txt')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setJobDescription(String(event.target.result ?? ''));
            };
            reader.onerror = () => {
                setError('Could not read this text file.');
                setJobDescriptionFile(null);
                if (jobDescriptionFileInputRef.current) {
                    jobDescriptionFileInputRef.current.value = '';
                }
            };
            reader.readAsText(file);
            return;
        }

        setExtractingFile(true);
        const fd = new FormData();
        fd.append('file', file);
        apiFetch('/api/jobs/extract-description', {
            method: 'POST',
            body: fd,
        })
            .then(async (res) => {
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(
                        getUserFacingErrorMessage(
                            res.status,
                            body,
                            parseApiErrorDetail(body) || 'Could not read this file.'
                        )
                    );
                }
                setJobDescription(String(body.text ?? ''));
            })
            .catch((err) => {
                setError(err.message || 'Could not read this file.');
                setJobDescriptionFile(null);
                if (jobDescriptionFileInputRef.current) {
                    jobDescriptionFileInputRef.current.value = '';
                }
            })
            .finally(() => {
                setExtractingFile(false);
            });
    };

    const buildFinalDescription = () => {
        if (jobDescriptionFile) {
            return `Job description from file: ${jobDescriptionFile.name}\n\n${jobDescription}`;
        }
        return jobDescription;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const finalText = buildFinalDescription().trim();
        if (!finalText && !jobDescriptionFile) {
            setError('Please provide a job description or upload a file');
            return;
        }

        if (!isExistingJob && !finalText) {
            setError('Please provide a job description or upload a file');
            return;
        }

        setCreating(true);
        setError(null);

        try {
            if (isExistingJob) {
                const baseline = (initialDescriptionRef.current ?? '').trim();
                const changed =
                    finalText !== baseline || Boolean(jobDescriptionFile);

                if (changed) {
                    const response = await apiFetch(
                        `/api/jobs/${existingJobId}/job-description`,
                        {
                            method: 'PUT',
                            body: JSON.stringify({ description: finalText }),
                        }
                    );
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(
                            getUserFacingErrorMessage(
                                response.status,
                                errorData,
                                parseApiErrorDetail(errorData) || 'Failed to update job description'
                            )
                        );
                    }
                    initialDescriptionRef.current = finalText;
                }

                navigate(`/jobs/${existingJobId}/setup-form`);
                return;
            }

            const formData = new FormData();
            // Textarea already holds extracted file content; avoid sending the
            // binary again so create does not append a duplicate extraction.
            formData.append('job_description', finalText);

            const response = await apiFetch('/api/jobs/create', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        errorData,
                        parseApiErrorDetail(errorData) || 'Failed to create job posting'
                    )
                );
            }

            const data = await response.json();
            navigate(`/jobs/${data.job_id}/setup-form`);
        } catch (err) {
            console.error('Error in job posting flow:', err);
            setError(
                err.message || 'Something went wrong. Please try again.'
            );
        } finally {
            setCreating(false);
        }
    };

    if (jobIdParam && !isExistingJob) {
        return (
            <div className="create-job-posting-page">
                <div className="create-job-container">
                    <div className="error-message">Invalid job link.</div>
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={() => navigate('/jobs')}
                    >
                        Back to jobs
                    </button>
                </div>
            </div>
        );
    }

    const disableForm =
        creating ||
        extractingFile ||
        (isExistingJob && pageLoading);

    return (
        <div className="create-job-posting-page">
            <div className="create-job-container">
                <div className="create-job-header">
                    <h1>
                        {isExistingJob
                            ? '📝 Application posting for this job'
                            : '📝 Create Job Posting'}
                    </h1>
                    <p>
                        {isExistingJob
                            ? 'Review or edit the job description, then set up your public application link. Candidates who apply will be added to this same job.'
                            : 'Create a new job posting and set up your application form for LinkedIn integration'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="create-job-form">
                    <div className="form-section">
                        <label htmlFor="job-description-file">
                            <strong>Upload Job Description File (Optional)</strong>
                        </label>
                        <div className="file-input-wrapper">
                            <input
                                ref={jobDescriptionFileInputRef}
                                type="file"
                                id="job-description-file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleFileChange}
                                className="file-input-hidden"
                                disabled={disableForm}
                            />
                            <label
                                htmlFor="job-description-file"
                                className="file-input-button"
                            >
                                Choose File
                            </label>
                            <span className="file-input-name">
                                {jobDescriptionFile
                                    ? jobDescriptionFile.name
                                    : 'No file chosen'}
                            </span>
                        </div>
                        <small>Supported formats: PDF, DOC, DOCX, TXT</small>
                        {extractingFile && (
                            <small className="file-extract-status">
                                Extracting text from file…
                            </small>
                        )}
                    </div>

                    <div className="form-section">
                        <label htmlFor="job-description">
                            <strong>Job Description *</strong>
                        </label>
                        <textarea
                            id="job-description"
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder="Enter job description here... (e.g., Job Title, Requirements, Responsibilities, etc.)"
                            rows={15}
                            required
                            className="job-description-textarea"
                            disabled={disableForm}
                        />
                        <small>
                            You can paste the job description here or upload a file
                            above
                        </small>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/jobs')}
                            className="cancel-button"
                            disabled={disableForm}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="create-button"
                            disabled={
                                disableForm ||
                                (!jobDescription.trim() && !jobDescriptionFile)
                            }
                        >
                            {creating
                                ? isExistingJob
                                    ? 'Saving...'
                                    : 'Creating...'
                                : 'Next →'}
                        </button>
                    </div>
                </form>

                <div className="info-box">
                    <h3>What happens next?</h3>
                    <ol>
                        {isExistingJob ? (
                            <>
                                <li>
                                    This job stays the same — no duplicate job is
                                    created
                                </li>
                                <li>
                                    You&apos;ll open the application form setup for
                                    this job
                                </li>
                                <li>
                                    Configure questions, greeting, and appearance
                                </li>
                                <li>
                                    Copy the application URL for LinkedIn or other
                                    boards
                                </li>
                            </>
                        ) : (
                            <>
                                <li>Your job posting will be created</li>
                                <li>
                                    You&apos;ll be taken to the job details page
                                </li>
                                <li>
                                    Click the &quot;📝 Application Form&quot; tab to
                                    set up your application form
                                </li>
                                <li>
                                    Configure greeting message, questions, and form
                                    appearance
                                </li>
                                <li>
                                    Copy the application URL and use it when posting
                                    on LinkedIn
                                </li>
                            </>
                        )}
                    </ol>
                </div>
            </div>
        </div>
    );
};

export default CreateJobPostingPage;
